import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Globe, Plug, Plus, Smartphone } from "lucide-react";
import { Card, Editorial, Eyebrow, LiveDot } from "@/ui";
import { cn } from "@/lib/cn";
import { useWorkspace, type WsProperty } from "@/app/WorkspaceContext";
import { useSummary } from "@/data/hooks";
import { KIND_LABEL, type PropertyKind } from "@/data/hooks";
import { account } from "@/mock";
import { fmt } from "@/lib/format";

const KIND_ORDER: PropertyKind[] = ["built", "connected", "app"];
const KIND_ICON = { built: Globe, connected: Plug, app: Smartphone } as const;

function PropertyCard({ p }: { p: WsProperty }) {
  const nav = useNavigate();
  const { setCurrent, live, range } = useWorkspace();
  const isReal = live && !!p.site;
  const summary = useSummary(isReal ? p.id : undefined, range);
  const visits = summary.data?.visits;
  const Icon = KIND_ICON[p.propertyKind];

  return (
    <Card
      interactive
      onClick={() => {
        setCurrent(p.id);
        nav("/signals");
      }}
      className="group p-4"
    >
      <div className="flex items-start justify-between">
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.hue }} />
          <span className="font-mono text-[10px] uppercase tracking-wide text-subtle">
            {KIND_LABEL[p.propertyKind]}
          </span>
        </span>
        <Icon className="h-4 w-4 text-subtle" strokeWidth={1.5} />
      </div>
      <p className="mt-2 text-[15px] font-semibold tracking-tight text-ink-strong">{p.name}</p>
      <p className="truncate font-mono text-[11px] text-subtle">{p.domain}</p>
      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="text-[22px] font-semibold leading-none text-ink-strong">
            {isReal ? (visits != null ? fmt(visits) : "—") : fmt(1240)}
          </p>
          <p className="mt-1 text-[11px] text-muted">visits · {range}</p>
        </div>
        <ArrowUpRight className="h-4 w-4 text-subtle transition-colors group-hover:text-ink" strokeWidth={1.5} />
      </div>
    </Card>
  );
}

function AddTile({ label, hint, icon: Icon, onClick }: { label: string; hint: string; icon: typeof Plus; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex min-h-[44px] flex-col items-start gap-2 rounded-card border border-dashed border-hairline bg-paper-2/40 p-4 text-left transition-colors hover:border-accent/40 hover:bg-paper-2 dark:bg-white/[0.02]"
    >
      <span className="grid h-9 w-9 place-items-center rounded-full border border-dashed border-hairline text-muted">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </span>
      <span className="text-[13px] font-medium text-ink-strong">{label}</span>
      <span className="text-[12px] leading-snug text-muted">{hint}</span>
    </button>
  );
}

export function HomeScreen() {
  const nav = useNavigate();
  const { properties, live, loading } = useWorkspace();
  const first = account.user.name.split(" ")[0];
  const grouped = KIND_ORDER.map((k) => ({ kind: k, items: properties.filter((p) => p.propertyKind === k) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="stagger space-y-7">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow>Your workspace · {account.workspace.name}</Eyebrow>
          <h1 className="mt-1 text-[26px] font-semibold tracking-tight text-ink-strong md:text-[32px]">
            Everything you run, {first}. <Editorial className="text-muted">one shelf.</Editorial>
          </h1>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-2 rounded-full border border-hairline px-3 py-1.5 text-[12px]",
            live ? "text-ink" : "text-muted",
          )}
        >
          {live ? <LiveDot /> : <span className="h-2 w-2 rounded-full bg-subtle" />}
          {loading ? "connecting…" : live ? "live data" : "sample data"}
        </span>
      </header>

      {grouped.map((g) => {
        const Icon = KIND_ICON[g.kind];
        return (
          <section key={g.kind} className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted" strokeWidth={1.5} />
              <h2 className="text-[14px] font-semibold tracking-tight text-ink-strong">{KIND_LABEL[g.kind]}s</h2>
              <span className="font-mono text-[11px] text-subtle">{g.items.length}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {g.items.map((p) => (
                <PropertyCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="space-y-3">
        <h2 className="text-[14px] font-semibold tracking-tight text-ink-strong">Add a property</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <AddTile label="Build a new site" hint="Nara builds + hosts it" icon={Globe} onClick={() => nav("/make")} />
          <AddTile label="Connect a site" hint="Track a site you already have" icon={Plug} onClick={() => nav("/you/integrations")} />
          <AddTile label="Add a mobile app" hint="iOS & Android SDK + replay" icon={Smartphone} onClick={() => nav("/sdk")} />
        </div>
      </section>
    </div>
  );
}
